import React, {memo} from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import MUIContainer from '@/components/MUI-Components/MUI-Container';
import {StaticImageData} from 'next/image';
import DisplayImage from '@/components/Ui-components/display-image';
import {contentMaxWidth, borderRadius, colors, fontWeight} from '@/styles/tokens';

export interface Web3TechStatCard {
    icon: StaticImageData;
    title: string;
    subtitle: string;
}

export interface Web3ChainItem {
    name: string;
    logo: StaticImageData;
}

export interface Web3TechCategory {
    title: string;
    tags: string[];
}

export interface Web3TechInfrastructureProps {
    heading: string;
    subheading: string;
    statCards: Web3TechStatCard[];
    blockchainSection: {
        title: string;
        subtitle: string;
        chains: Web3ChainItem[];
    };
    techCategories: Web3TechCategory[];
}

const sectionHeadingSx = {
    textAlign: 'center',
    mb: 5
} as const;

const headingTypoSx = {
    fontSize: {xs: '32px', md: '46px'},
    fontWeight: fontWeight.bold,
    color: colors.text.primary,
    lineHeight: {xs: '42px', md: '56px'},
    mb: 2,
    letterSpacing: '-0.5px'
} as const;

const subheadingTypoSx = {
    fontSize: '16px',
    fontWeight: fontWeight.regular,
    color: colors.text.muted,
    lineHeight: '28px',
    maxWidth: contentMaxWidth.standard,
    mx: 'auto'
} as const;

const innerPanelSx = {
    bgcolor: colors.background.slate50,
    borderRadius: borderRadius['2xl'],
    p: {xs: '24px', md: '32px'},
    display: 'flex',
    flexDirection: 'column',
    gap: '20px'
} as const;

const statGridSx = {
    display: 'grid',
    gridTemplateColumns: {xs: '1fr', sm: 'repeat(3, 1fr)'},
    gap: '16px'
} as const;

const statCardSx = {
    bgcolor: '#fff',
    border: '1px solid #eaecf0',
    borderRadius: borderRadius.xl,
    px: '21px',
    py: '19px',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    boxShadow: '0px 6px 10px rgba(16,24,40,0.05)',
    background: 'linear-gradient(180deg, #fff 0%, #f8fafc 100%)'
} as const;

const statIconBgSx = {
    bgcolor: colors.background.tag,
    borderRadius: borderRadius.lg,
    width: 40,
    height: 40,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0
} as const;

const chainCardSx = {
    bgcolor: '#fff',
    border: '1px solid #eaecf0',
    borderRadius: borderRadius.xl,
    p: '25px',
    boxShadow: '0px 8px 12px rgba(16,24,40,0.05)',
    display: 'flex',
    flexDirection: 'column',
    gap: '20px'
} as const;

const chainGridSx = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(88px, 1fr))',
    gap: '10px'
} as const;

const chainItemSx = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '10px',
    bgcolor: '#fff',
    borderRadius: borderRadius['14']
} as const;

const chainLogoBgSx = {
    width: '100%',
    border: '1px solid #eaecf0',
    borderRadius: borderRadius.lg,
    background: 'linear-gradient(180deg, #fff 0%, #f8fafc 100%)',
    height: 72,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
} as const;

const techGridSx = {
    display: 'grid',
    gridTemplateColumns: {xs: '1fr', sm: 'repeat(2, 1fr)'},
    gap: '24px'
} as const;

const techCategorySx = {
    bgcolor: '#fff',
    border: '1px solid rgba(195,198,214,0.15)',
    borderRadius: borderRadius.xl,
    px: '25px',
    pt: '24px',
    pb: '25px',
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
    boxShadow: '0px 1px 1px rgba(0,0,0,0.05)'
} as const;

const categoryLabelSx = {
    fontSize: '13px',
    fontWeight: fontWeight.semibold,
    color: colors.text.primary,
    letterSpacing: '0.65px',
    textTransform: 'uppercase' as const,
    lineHeight: '19.5px'
} as const;

const tagWrapSx = {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: '8px'
} as const;

const tagSx = {
    bgcolor: '#fff',
    border: '1px solid rgba(195,198,214,0.3)',
    borderRadius: borderRadius.full,
    px: '15px',
    py: '9px',
    display: 'inline-flex',
    alignItems: 'center'
} as const;

const tagTextSx = {
    fontSize: '14px',
    fontWeight: fontWeight.medium,
    color: colors.text.primary,
    lineHeight: '20px',
    whiteSpace: 'nowrap' as const
} as const;

const Web3TechInfrastructure = ({heading, subheading, statCards, blockchainSection, techCategories}: Web3TechInfrastructureProps) => {
    return (
        <MUIContainer>
            <Box sx={sectionHeadingSx}>
                <Typography variant="h1" component="h2" className="font-headers" sx={headingTypoSx}>
                    {heading}
                </Typography>
                <Typography className="font-regular-text" sx={subheadingTypoSx}>
                    {subheading}
                </Typography>
            </Box>

            <Box sx={innerPanelSx}>
                {/* Stat cards */}
                <Box sx={statGridSx}>
                    {statCards.map((card) => (
                        <Box key={card.title} sx={statCardSx}>
                            <Box sx={statIconBgSx}>
                                <Box sx={{width: 20, height: 20, position: 'relative', flexShrink: 0}}>
                                    <DisplayImage src={card.icon} alt="" fill />
                                </Box>
                            </Box>
                            <Box sx={{display: 'flex', flexDirection: 'column', gap: '2px'}}>
                                <Typography
                                    className="font-headers"
                                    sx={{
                                        fontSize: '15px',
                                        fontWeight: fontWeight.semibold,
                                        color: colors.text.primary,
                                        lineHeight: '21px'
                                    }}
                                >
                                    {card.title}
                                </Typography>
                                <Typography
                                    className="font-regular-text"
                                    sx={{
                                        fontSize: '13px',
                                        fontWeight: fontWeight.regular,
                                        color: colors.text.secondary,
                                        lineHeight: '18px'
                                    }}
                                >
                                    {card.subtitle}
                                </Typography>
                            </Box>
                        </Box>
                    ))}
                </Box>

                {/* Blockchain networks card */}
                <Box sx={chainCardSx}>
                    <Box sx={{display: 'flex', flexDirection: 'column', gap: '7px'}}>
                        <Typography
                            className="font-headers"
                            sx={{fontSize: '20px', fontWeight: fontWeight.semibold, color: colors.text.primary, lineHeight: '27px'}}
                        >
                            {blockchainSection.title}
                        </Typography>
                        <Typography
                            className="font-regular-text"
                            sx={{fontSize: '14px', fontWeight: fontWeight.regular, color: colors.text.primary, lineHeight: '22px'}}
                        >
                            {blockchainSection.subtitle}
                        </Typography>
                    </Box>
                    <Box sx={chainGridSx}>
                        {blockchainSection.chains.map((chain) => (
                            <Box key={chain.name} sx={chainItemSx}>
                                <Box sx={chainLogoBgSx}>
                                    <Box sx={{width: 28, height: 28, position: 'relative', flexShrink: 0}}>
                                        <DisplayImage src={chain.logo} alt={chain.name} fill />
                                    </Box>
                                </Box>
                                <Typography
                                    className="font-regular-text"
                                    sx={{
                                        fontSize: '13px',
                                        fontWeight: fontWeight.regular,
                                        color: '#344054',
                                        lineHeight: '17.5px',
                                        textAlign: 'center'
                                    }}
                                >
                                    {chain.name}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                </Box>

                {/* Tech category cards */}
                <Box sx={techGridSx}>
                    {techCategories.map((cat) => (
                        <Box key={cat.title} sx={techCategorySx}>
                            <Typography sx={categoryLabelSx}>{cat.title}</Typography>
                            <Box sx={tagWrapSx}>
                                {cat.tags.map((tag) => (
                                    <Box key={tag} sx={tagSx}>
                                        <Typography sx={tagTextSx}>{tag}</Typography>
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                    ))}
                </Box>
            </Box>
        </MUIContainer>
    );
};

export default memo(Web3TechInfrastructure);
